/**
 * APP COLOR TOKENS
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source of truth for all colors across the application.
 *
 * STANDARD: Tailwind class pairs (light + dark:xxx) for everything in className.
 * HEX:      Only where Tailwind classes cannot be used:
 *             - Chart color configs
 *             - Inline style gradients / glow box-shadows
 *             - VDI stepper hex string-comparison logic
 *
 * Exports (in order of how often you will need them):
 *   APP_BG       - surface backgrounds (9 levels)
 *   APP_SEMANTIC - intent bundles: bg, text, border, badge, dot, hover, close
 *   APP_BORDER   - border class pairs (3 strengths)
 *   APP_TEXT     - text color pairs
 *   APP_ACCENT   - multi-color palette for pickers (checkbox, radio, spinner)
 *   APP_GRADIENT - inline gradient strings for style={{ background }}
 *   APP_STYLE    - full inline style objects
 *   APP_RIPPLE   - button ripple overlay rgba colors
 *   APP_VDI      - VDI stepper step-state hex values
 *   APP_HEX      - raw hex (charts, glow, inline only) → see hex.constant.ts
 *
 * Usage:
 *   import { APP_BG, APP_BORDER, APP_SEMANTIC } from "@/constant/theme/colors.constant";
 *   <div className={`rounded-xl ${APP_BG.base} border ${APP_BORDER.default}`} />
 */

// ─── Surface Backgrounds ───────────────────────────────────────────────────────
// 13 semantic keys — same hex dark values, 25 → 13. Always use in className.

export const APP_BG = {
  // ─── Core surfaces ─────────────────────────────────────────────────────────
  /** App canvas / page shell / 404 / auth page (was: page, auth, surface) */
  canvas: 'bg-gray-100 dark:bg-[#07090d]',
  /** Cards, modals, table body, login card (was: base, authCard, dashBody) */
  base: 'bg-white dark:bg-[#090d12]',
  /** Section headers, panels, expand rows, alt rows (was: panel, raised, deep, dashPanel) */
  surface: 'bg-gray-50 dark:bg-[#0c131a]',
  /** SectionCard header band — slightly darker than card body */
  sectionHeader: 'bg-gray-100 dark:bg-[#0a0f14]',
  /** Toolbar strip, header dropdowns, action bars (was: toolbar, footer, headerPanel) */
  elevated: 'bg-white dark:bg-[#0c1220]',
  /** Code / payload mono viewer */
  code: 'bg-white dark:bg-[#060a0f]',
  /** Form inputs, autocomplete fields */
  input: 'bg-white dark:bg-[#090d14]',
  /** Modal / export backdrop (was: overlay, surfaceOverlay*) */
  overlay: 'bg-white/90 dark:bg-[#090d12]/90',
  /** Disabled / neutral muted blocks (was: surfaceMuted) */
  muted: 'bg-gray-100 dark:bg-[#090d12]',
  filter: 'bg-white/20 dark:bg-[#090d12]',
  // ─── Always-dark sidebar ───────────────────────────────────────────────────
  /** Sidebar rail */
  sidebar: 'bg-[#05070a]',
  /** Sidebar floating panel / popover (was: sidebarPanel) */
  sidebarPopover: 'bg-[#0b0f16]',
  /** Sidebar item hover */
  sidebarHover: 'hover:bg-[#121826]',

  // ─── Always-dark structural ────────────────────────────────────────────────
  /** Sticky table header */
  tableHeader: 'bg-slate-200 dark:bg-[#071428]',
  /** Tooltip bubble */
  tooltip: 'bg-gray-900',
  /** Toast / DashboardSkeleton card surface */
  card: 'bg-white dark:bg-[#0f1623]',
  /** Dropdown / context-menu popover (RowActionsMenu) */
  popover: 'bg-white dark:bg-[#0f1117]',
  /** CodeViewer gutter / title bar / status bar */
  codeMeta: 'bg-gray-50 dark:bg-[#0a0e14]',
  /** CodeViewer gutter with backdrop-blur opacity */
  codeMetaBlur: 'bg-gray-50/80 dark:bg-[#0a0e14]/80',
  /** CodeViewer editable textarea area */
  codeArea: 'bg-gray-50 dark:bg-[#060a0f]',
} as const;

// ─── Semantic Color Bundles ────────────────────────────────────────────────────
// Complete color set per semantic intent.
// bg, text, border  → layout classes
// badge             → pill / chip class string (includes bg + text + border)
// ring              → focus ring class string
// dot               → status dot background class
// hover             → hover background class
// close             → dismiss/x-button hover classes

export const APP_SEMANTIC = {
  danger: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-500 dark:text-red-400',
    border: 'border-red-200 dark:border-red-700/60',
    badge:
      'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
    ring: 'ring-1 ring-inset ring-red-200 dark:ring-red-700',
    dot: 'bg-red-500',
    hover: 'hover:bg-red-100 dark:hover:bg-red-900/40',
    close:
      'hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400',
  },
  success: {
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    text: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-700/60',
    badge:
      'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
    ring: 'ring-1 ring-inset ring-emerald-200 dark:ring-emerald-700',
    dot: 'bg-emerald-500',
    hover: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/40',
    close:
      'hover:bg-green-50 dark:hover:bg-green-900/30 hover:text-green-600 dark:hover:text-green-400',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-700/60',
    badge:
      'bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    ring: 'ring-1 ring-inset ring-amber-200 dark:ring-amber-700',
    dot: 'bg-amber-500',
    hover: 'hover:bg-amber-100 dark:hover:bg-amber-900/40',
    close:
      'hover:bg-amber-50 dark:hover:bg-amber-900/30 hover:text-amber-600 dark:hover:text-amber-400',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-700/60',
    badge:
      'bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    ring: 'ring-1 ring-inset ring-blue-200 dark:ring-blue-700',
    dot: 'bg-blue-500',
    hover: 'hover:bg-blue-100 dark:hover:bg-blue-900/40',
    close:
      'hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400',
  },
  purple: {
    bg: 'bg-violet-50 dark:bg-violet-950/40',
    text: 'text-violet-600 dark:text-violet-400',
    border: 'border-violet-200 dark:border-violet-700/50',
    badge:
      'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 border-violet-200 dark:border-violet-800',
    ring: 'ring-violet-400',
    dot: 'bg-violet-500',
    hover: 'hover:bg-violet-100 dark:hover:bg-violet-900/40',
    close:
      'hover:bg-violet-50 dark:hover:bg-violet-900/30 hover:text-violet-600 dark:hover:text-violet-400',
  },
  neutral: {
    bg: 'bg-gray-100 dark:bg-white/[0.07]',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border-gray-200 dark:border-white/[0.10]',
    badge:
      'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700',
    ring: 'ring-gray-400',
    dot: 'bg-gray-400',
    hover: 'hover:bg-gray-200/80 dark:hover:bg-white/[0.13]',
    close: 'hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700',
  },
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-700/60',
    badge:
      'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    ring: 'ring-blue-400',
    dot: 'bg-blue-500',
    hover: 'hover:bg-blue-100 dark:hover:bg-blue-900/40',
    close:
      'hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400',
  },
  orange: {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    text: 'text-orange-600 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-800/50',
    badge:
      'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800',
    ring: 'ring-orange-400',
    dot: 'bg-orange-500',
    hover: 'hover:bg-orange-100 dark:hover:bg-orange-900/40',
    close:
      'hover:bg-orange-50 dark:hover:bg-orange-900/30 hover:text-orange-600 dark:hover:text-orange-400',
  },
  rose: {
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    text: 'text-rose-600 dark:text-rose-400',
    border: 'border-rose-200 dark:border-rose-700/60',
    badge:
      'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800',
    ring: 'ring-rose-400',
    dot: 'bg-rose-500',
    hover: 'hover:bg-rose-100 dark:hover:bg-rose-900/40',
    close:
      'hover:bg-rose-50 dark:hover:bg-rose-900/30 hover:text-rose-600 dark:hover:text-rose-400',
  },
  sky: {
    bg: 'bg-sky-50 dark:bg-sky-900/20',
    text: 'text-sky-700 dark:text-sky-400',
    border: 'border-sky-200 dark:border-sky-700/60',
    badge:
      'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-800',
    ring: 'ring-sky-400',
    dot: 'bg-sky-400',
    hover: 'hover:bg-sky-100 dark:hover:bg-sky-900/40',
    close:
      'hover:bg-sky-50 dark:hover:bg-sky-900/30 hover:text-sky-600 dark:hover:text-sky-400',
  },
  indigo: {
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
    text: 'text-indigo-700 dark:text-indigo-400',
    border: 'border-indigo-200 dark:border-indigo-700/60',
    badge:
      'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800',
    ring: 'ring-indigo-400',
    dot: 'bg-indigo-500',
    hover: 'hover:bg-indigo-100 dark:hover:bg-indigo-900/40',
    close:
      'hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-600 dark:hover:text-indigo-400',
  },
  teal: {
    bg: 'bg-teal-50 dark:bg-teal-900/20',
    text: 'text-teal-700 dark:text-teal-400',
    border: 'border-teal-200 dark:border-teal-700/60',
    badge:
      'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 border-teal-200 dark:border-teal-800',
    ring: 'ring-teal-400',
    dot: 'bg-teal-500',
    hover: 'hover:bg-teal-100 dark:hover:bg-teal-900/40',
    close:
      'hover:bg-teal-50 dark:hover:bg-teal-900/30 hover:text-teal-600 dark:hover:text-teal-400',
  },
  cyan: {
    bg: 'bg-cyan-50 dark:bg-cyan-900/20',
    text: 'text-cyan-700 dark:text-cyan-400',
    border: 'border-cyan-200 dark:border-cyan-700/60',
    badge:
      'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 border-cyan-200 dark:border-cyan-800',
    ring: 'ring-cyan-400',
    dot: 'bg-cyan-500',
    hover: 'hover:bg-cyan-100 dark:hover:bg-cyan-900/40',
    close:
      'hover:bg-cyan-50 dark:hover:bg-cyan-900/30 hover:text-cyan-600 dark:hover:text-cyan-400',
  },
  fuchsia: {
    bg: 'bg-fuchsia-50 dark:bg-fuchsia-900/20',
    text: 'text-fuchsia-700 dark:text-fuchsia-400',
    border: 'border-fuchsia-200 dark:border-fuchsia-700/60',
    badge:
      'bg-fuchsia-100 dark:bg-fuchsia-900/30 text-fuchsia-700 dark:text-fuchsia-400 border-fuchsia-200 dark:border-fuchsia-800',
    ring: 'ring-fuchsia-400',
    dot: 'bg-fuchsia-500',
    hover: 'hover:bg-fuchsia-100 dark:hover:bg-fuchsia-900/40',
    close:
      'hover:bg-fuchsia-50 dark:hover:bg-fuchsia-900/30 hover:text-fuchsia-600 dark:hover:text-fuchsia-400',
  },
} as const;

// ─── Border Class Pairs ────────────────────────────────────────────────────────
// 3 strengths cover all structural borders.
// Semantic intent borders (danger, success, etc.) live in APP_SEMANTIC.*.border

export const APP_BORDER = {
  /** Standard card borders, dividers */
  default: 'border-gray-200 dark:border-gray-700',
  /** Subtle inner dividers, row separators */
  soft: 'border-gray-100 dark:border-gray-700/60',
  /** Standard divide (grid/flex cell separators) */
  divideDefault: 'divide-gray-200 dark:divide-gray-700',
  /** Subtle divide (inner row separators) */
  divideSoft: 'divide-gray-100 dark:divide-gray-700/60',
  /** Thin 1px rule background — for <div> hairline dividers */
  dividerBg: 'bg-gray-200 dark:bg-white/[0.07]',
  /** Card / panel outer border (toolbar, popovers) */
  card: 'border-gray-200/80 dark:border-white/[0.07]',
  /** Card body border (FullPageCard sides + bottom) */
  cardBody: 'border-gray-200/80 dark:border-white/10',
  /** Filter row top separator */
  filterRow: 'border-gray-100 dark:border-white/[0.05]',
  /** Dashboard notification dot border */
  dashDot: 'border-white dark:border-[#0a0e17]',
  /** Toolbar/profile status dot border */
  toolbarDot: 'border-white dark:border-[#0c1220]',
  /** Timeline step dot ring (must match base card bg) */
  timelineDotRing: 'ring-white dark:ring-[#090d12]',
} as const;

// ─── Text Colors ───────────────────────────────────────────────────────────────

export const APP_TEXT = {
  default: 'text-gray-900 dark:text-white',
  soft: 'text-gray-700 dark:text-gray-300',
  muted: 'text-gray-500 dark:text-gray-400',
  subtle: 'text-gray-400 dark:text-gray-500',
  faint: 'text-gray-300 dark:text-gray-600',
  primary: 'text-primary',
  secondary: 'text-secondary',
  white: 'text-white',
  'white-soft': 'text-white/70',
  'white-muted': 'text-white/50',
  'white-dim': 'text-white/40',
  danger: 'text-red-500 dark:text-red-400',
  success: 'text-emerald-600 dark:text-emerald-400',
  warning: 'text-amber-500 dark:text-amber-400',
  info: 'text-blue-500 dark:text-blue-400',
  indigo: 'text-indigo-500 dark:text-indigo-400',
  inherit: 'text-inherit',
  /** Code viewer body text (Catppuccin Mocha dark) */
  code: 'text-gray-700 dark:text-[#cdd6f4]',
} as const;

// ─── Accent Color Palette ──────────────────────────────────────────────────────
// Per-hue bg/border/text/ring/hover for multi-color picker components.
// Used by: AppCheckbox, AppRadio, Spinner, VDI/Cooler status icons.

export const APP_ACCENT = {
  primary: {
    bg: 'bg-primary',
    border: 'border-primary',
    text: 'text-primary',
    ring: 'ring-primary/30',
    hover: 'hover:border-primary/80',
  },
  blue: {
    bg: 'bg-blue-500',
    border: 'border-blue-500',
    text: 'text-blue-500',
    ring: 'ring-blue-500/30',
    hover: 'hover:border-blue-400',
  },
  purple: {
    bg: 'bg-purple-500',
    border: 'border-purple-500',
    text: 'text-purple-500',
    ring: 'ring-purple-500/30',
    hover: 'hover:border-purple-400',
  },
  green: {
    bg: 'bg-green-500',
    border: 'border-green-500',
    text: 'text-green-500',
    ring: 'ring-green-500/30',
    hover: 'hover:border-green-400',
  },
  red: {
    bg: 'bg-red-500',
    border: 'border-red-500',
    text: 'text-red-500',
    ring: 'ring-red-500/30',
    hover: 'hover:border-red-400',
  },
  orange: {
    bg: 'bg-orange-500',
    border: 'border-orange-500',
    text: 'text-orange-500',
    ring: 'ring-orange-500/30',
    hover: 'hover:border-orange-400',
  },
  teal: {
    bg: 'bg-teal-500',
    border: 'border-teal-500',
    text: 'text-teal-500',
    ring: 'ring-teal-500/30',
    hover: 'hover:border-teal-400',
  },
  indigo: {
    bg: 'bg-indigo-500',
    border: 'border-indigo-500',
    text: 'text-indigo-500',
    ring: 'ring-indigo-500/30',
    hover: 'hover:border-indigo-400',
  },
  emerald: {
    bg: 'bg-emerald-500',
    border: 'border-emerald-500',
    text: 'text-emerald-500',
    ring: 'ring-emerald-500/30',
    hover: 'hover:border-emerald-400',
  },
  amber: {
    bg: 'bg-amber-500',
    border: 'border-amber-500',
    text: 'text-amber-500',
    ring: 'ring-amber-500/30',
    hover: 'hover:border-amber-400',
  },
  gray: {
    bg: 'bg-gray-500',
    border: 'border-gray-500',
    text: 'text-gray-500',
    ring: 'ring-gray-500/30',
    hover: 'hover:border-gray-400',
  },
} as const;

// ─── Inline Gradients ─────────────────────────────────────────────────────────
// For style={{ background }} only — when Tailwind gradient classes cannot be used.

export const APP_GRADIENT = {
  /** 2-stop: accepts explicit hex values */
  primary: (p: string, s: string) =>
    `linear-gradient(135deg, ${p} 0%, ${s} 100%)`,
  /** 3-stop: accepts explicit hex values */
  brand: (p: string, s: string, t: string) =>
    `linear-gradient(135deg, ${p} 0%, ${s} 60%, ${t} 100%)`,
  /** 2-stop using CSS vars — always tracks theme, no useTheme needed */
  primaryVar:
    'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
  /** 3-stop using CSS vars — always tracks theme, no useTheme needed */
  brandVar:
    'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 60%, var(--color-tertiary) 100%)',
  danger: 'linear-gradient(135deg,#ef4444 0%,#dc2626 100%)',
  success: 'linear-gradient(135deg,#10b981 0%,#059669 100%)',
  warning: 'linear-gradient(135deg,#f59e0b 0%,#d97706 100%)',
  purple: 'linear-gradient(135deg,#8b5cf6 0%,#7c3aed 100%)',
};

// ─── Inline Style Objects ─────────────────────────────────────────────────────

export const APP_STYLE = {
  /** SmartCooler product image area gradient */
  smartCoolerHeader: {
    backgroundImage: 'linear-gradient(to bottom, #0b1118 0%, #0c131a 100%)',
  },
} as const;

// ─── Button Ripple Colors ─────────────────────────────────────────────────────
// Translucent rgba overlays for AppButton ripple effect per variant.

export const APP_RIPPLE = {
  white: 'rgba(255,255,255,0.30)',
  primary: (p: string) => `${p}28`,
  danger: 'rgba(239,68,68,0.15)',
  success: 'rgba(16,185,129,0.15)',
  warning: 'rgba(245,158,11,0.15)',
  info: 'rgba(99,102,241,0.15)',
  purple: 'rgba(139,92,246,0.15)',
  blue: 'rgba(59,130,246,0.15)',
  neutral: 'rgba(0,0,0,0.08)',
  dark: 'rgba(0,0,0,0.10)',
} as const;

// ─── VDI Step-State Hex ───────────────────────────────────────────────────────
// Hex strings for VDI / Cooler stepper step-state comparison logic.

export const APP_VDI = {
  done: '#FF7F30',
  failed: '#E75065',
  inProgress: '#6366f1',
  /** Tailwind arbitrary-value bg classes – use in className strings */
  doneBg: 'bg-[#FF7F30]',
  failedBg: 'bg-[#E75065]',
  inProgressBg: 'bg-indigo-500',
} as const;

// ─── CodeViewer Syntax Token Classes ────────────────────────────────────────
// Catppuccin Mocha palette for dark mode; standard Tailwind hues in light mode.
// Use as: const T = APP_CODE_SYNTAX; then T.key, T.str, etc.

export const APP_CODE_SYNTAX = {
  key: 'text-indigo-600 dark:text-[#89b4fa]',
  str: 'text-emerald-600 dark:text-[#a6e3a1]',
  num: 'text-amber-600 dark:text-[#fab387]',
  bool: 'text-violet-600 dark:text-[#cba6f7]',
  nil: 'text-rose-500 dark:text-[#f38ba8]',
  bracket: 'text-gray-400 dark:text-[#6c7086]',
  count: 'text-gray-400 dark:text-[#585b70] bg-gray-100 dark:bg-white/[0.07]',
} as const;

// ─── Raw Hex Values ───────────────────────────────────────────────────────────
// Moved to hex.constant.ts for clarity. Re-exported here for convenience.
export { APP_HEX } from './hex.constant';
